<template>
  <div v-loading="isLoading" class="dashboard-charts card">
    <div class="card__block">
      <div class="card__header">
        <strong>
          {{ chartData.title }}
        </strong>
        <Hint v-if="chartData.description" :message="chartData.description" />
        <StatsAnnouncement
          v-if="chartData.announcement"
          :title="chartData.announcement.title"
          :text="chartData.announcement.text"
          class="dashboard-charts__announcement-button"
        />
      </div>
      <div v-if="!isLoading" class="dashboard-charts__content">
        <ChartUnavailable
          v-if="chartData.unavailable"
          class="dashboard-charts__unavailable"
          :chart-data="chartData"
        />
        <div class="dashboard-charts__details">
          <div class="dashboard-charts__detail-item">
            <template v-if="!hasRating || !hasSummary">
              <span>
                {{ chartData.date }}
              </span>
              <ElTooltip
                v-if="dateDescription"
                placement="top"
                :content="dateDescription"
                effect="light"
                class="dashboard-charts__date-description"
              >
                <small>
                  <i class="el-icon-question" />
                </small>
              </ElTooltip>
            </template>
            <template v-if="hasSummary">
              <StrongLabel class="dashboard-charts__detail-value" size="large">
                {{ chartData.summary.today }}
              </StrongLabel>
              <ElRate
                v-if="hasRating"
                :value="rating"
                disabled
                text-color="#ff9900"
                score-template="{value}"
              />
            </template>
          </div>
          <div v-if="hasSummary" class="dashboard-charts__detail-item">
            <span>
              {{ $t('APPS.DASHBOARD.PROGRESS') }}
            </span>
            <StrongLabel
              :kind="kind"
              size="small"
              class="dashboard-charts__detail-value"
            >
              {{ chartData.summary.growth }}%
            </StrongLabel>
          </div>
        </div>
        <AppChart :options="chartOptions" />
        <div v-if="hasMoreConfig" class="dashboard-charts__details-link">
          <RouterLink
            :to="{
              name: 'stats-builder',
              params: { config: chartData.config },
            }"
          >
            {{ $t('APPS.DASHBOARD.CHARTS.DETAILS_TITLE') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StrongLabel from '@@/common/StrongLabel';
import Hint from '@@/common/Hint';

import chart from '@/mixins/chart';

import AppChart from '@@/common/stats/AppChart';
import ChartUnavailable from './ChartUnavailable';
import StatsAnnouncement from '../StatsAnnouncement';

import Color from '@/utils/color';

export default {
  name: 'AppDashboardChart',
  components: {
    StrongLabel,
    AppChart,
    Hint,
    ChartUnavailable,
    StatsAnnouncement,
  },
  mixins: [chart],
  props: {
    chartData: {
      validator: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasRating: {
      type: Boolean,
      default: false,
    },
    isPositiveProgress: {
      type: Boolean,
      default: true,
    },
    yAxisTitle: {
      type: String,
      default: '',
    },
    dateDescription: {
      type: String,
      default: '',
    },
  },
  computed: {
    hasSummary() {
      return Boolean(
        this.chartData.summary?.today && this.chartData.summary?.growth,
      );
    },
    hasMoreConfig() {
      return Boolean(this.chartData.config);
    },
    rating() {
      return Number(this.chartData.summary.today);
    },
    series() {
      return this.chartData.series || [];
    },
    dates() {
      return this.chartData.dates || [];
    },
    chartOptions() {
      const isPersian = this.lang === 'fa';
      const dir = isPersian ? 'rtl' : 'ltr';

      return {
        chart: {
          type: 'line',
          height: 200,
        },

        legend: {
          layout: 'horizontal',
          align: 'left',
        },
        colors: Color.get(this.series.length).byStep(2, 15),
        xAxis: {
          categories: this.dates,
          title: this.$t('APPS.DASHBOARD.CHARTS.X_AXIS_TITLE'),
          tickPositions: [4, 11, 18, 25],
          labels: {},
        },
        yAxis: {
          title: {
            text: this.chartData.config.chart_dimensions['y-axis'].title,
          },
        },
        tooltip: {
          useHTML: true,
          style: {
            direction: dir,
          },
          pointFormatter() {
            const titlePostfix =
              this.series.userOptions?.data_title_postfix || '';

            return `<div>
                      <span style="color:${this.color}">\u25CF</span>
                      <span style="color:${this.color}">${this.series.name}: </span>
                      <span>${this.y}</span> <span>${titlePostfix}</span>
                    </div>`;
          },
        },
        series: this.series,
      };
    },
    hasPositiveProgress() {
      const hasPositiveGrowth =
        this.chartData.summary.growth.indexOf('+') !== -1;
      return this.isPositiveProgress ? hasPositiveGrowth : !hasPositiveGrowth;
    },
    // kind of the strong label
    kind() {
      return this.hasPositiveProgress ? 'success' : 'danger';
    },
  },
};
</script>
<style lang="scss">
.dashboard-charts {
  &__content {
    position: relative;
  }

  &__unavailable {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    text-align: center;
  }

  &__description {
    margin-top: $global-margin;
    font-size: $small-font-size;
  }

  &__date-description {
    margin-right: $global-margin / 4;
  }

  &__details {
    display: flex;
    align-items: center;
    font-size: $small-font-size;
    min-height: 70px;
  }

  &__detail-item {
    padding: $global-padding $global-padding / 2;

    display: flex;
    align-items: center;
  }

  &__detail-value {
    direction: ltr; //for sign of numbers

    &:first-child {
      margin-left: calc($global-margin / 2);
    }

    &:last-child {
      margin-right: calc($global-margin / 2);
    }
  }

  &__details-link {
    font-size: $small-font-size;
  }

  &__announcement-button {
    margin-left: calc($global-margin / 2);
    @include rtl() {
      margin-left: 0;
      margin-right: calc($global-margin / 2);
    }
    @include breakpoint(small down) {
      margin: 0 !important;
    }
  }
}
</style>
